export const APP_NAME = 'Le Comptoir';
export const APP_PUBLISHER = 'Stelvyo';
export const APP_DESCRIPTION = "Mise en relation entre candidats et entreprises de l'hôtellerie, de la restauration et du tourisme";
export const ROLES = ['student', 'company', 'responsable', 'super_admin'];
// Statuts du profil candidat — V1.0 (mai 2026).
// `fulfilled` reste accepté pendant la phase expand/contract de la migration
// (90 jours minimum) mais doit être traité comme un alias de `paused` côté UI
// et code. Voir docs/cahier-des-charges-v3.md §4.2.2 et §5.1.
export const STUDENT_STATUS = [
    'draft',
    'published',
    'paused',
    'placed',
    'archived',
    'fulfilled', // @deprecated — alias de `paused` pendant migration
];
/**
 * Statuts "actifs" sans la valeur dépréciée — à utiliser dans les nouvelles
 * surfaces UI (filtres, KPIs, formulaires de choix de statut).
 */
export const STUDENT_STATUS_ACTIVE = [
    'draft',
    'published',
    'paused',
    'placed',
    'archived',
];
/**
 * Helper : normalise les statuts dépréciés vers leur équivalent actif.
 * Utiliser côté UI et logique métier pour traiter `fulfilled` comme `paused`
 * pendant la phase expand/contract.
 */
export function normalizeStudentStatus(status) {
    return status === 'fulfilled' ? 'paused' : status;
}
export const COMPANY_VALIDATION_STATUS = ['pending', 'validated', 'rejected'];
export const CV_SCAN_STATUS = ['pending', 'clean', 'infected', 'error'];
export const PROMOTION_STATUS = ['active', 'archived'];
export const FORMATION_LEVELS = ['L3', 'M1', 'M2'];
export const CONSENT_TYPES = [
    'visibility_companies',
    'photo_display',
    'statistics_nominal',
    'cookies_stats',
];
export const CONSULTATION_TYPES = ['view', 'download'];
export const FORMATIONS_INITIAL = [
    { code: 'L3-MHR', label: 'Licence 3 — Management Hôtellerie-Restauration', level: 'L3' },
    { code: 'L3-IHR', label: "Licence 3 — Innovation Hôtellerie-Restauration", level: 'L3' },
    { code: 'L3-MRC', label: 'Licence 3 — Management de la Restauration Collective', level: 'L3' },
    { code: 'M1-MHR', label: 'Master 1 — Management Hôtellerie-Restauration', level: 'M1' },
    { code: 'M2-MHR', label: 'Master 2 — Management Hôtellerie-Restauration', level: 'M2' },
];
// Hiérarchie : 5 macro-secteurs (parentCode = null) + 27 sous-secteurs.
// Les profils existants pointant vers un macro restent valides ; les
// nouveaux profils peuvent cocher au niveau macro OU sous-secteur.
export const SECTORS_INITIAL = [
    // Macro-secteurs
    { code: 'hotellerie', label: 'Hôtellerie', parentCode: null },
    { code: 'restauration-commerciale', label: 'Restauration commerciale', parentCode: null },
    { code: 'restauration-collective', label: 'Restauration collective', parentCode: null },
    { code: 'conseil-ingenierie', label: "Bureau d'études & conseil, ingénierie", parentCode: null },
    { code: 'hospitalite-elargie', label: 'Hospitalité élargie', parentCode: null },
    // Sous-secteurs : Hôtellerie
    { code: 'hot-palace-5', label: 'Palace 5★', parentCode: 'hotellerie' },
    { code: 'hot-4', label: 'Hôtel 4★', parentCode: 'hotellerie' },
    { code: 'hot-2-3', label: 'Hôtel 2-3★', parentCode: 'hotellerie' },
    { code: 'hot-boutique', label: 'Hôtel indépendant / boutique-hôtel', parentCode: 'hotellerie' },
    { code: 'hot-plein-air', label: 'Hôtellerie de plein air / camping', parentCode: 'hotellerie' },
    { code: 'hot-residence', label: 'Résidence de tourisme / villages vacances', parentCode: 'hotellerie' },
    // Sous-secteurs : Restauration commerciale
    { code: 'rc-gastronomique', label: 'Gastronomique / étoilé', parentCode: 'restauration-commerciale' },
    { code: 'rc-bistronomique', label: 'Bistronomique / brasserie haut de gamme', parentCode: 'restauration-commerciale' },
    { code: 'rc-bistrot', label: 'Bistrot / brasserie traditionnelle', parentCode: 'restauration-commerciale' },
    { code: 'rc-rapide', label: 'Restauration rapide / QSR', parentCode: 'restauration-commerciale' },
    { code: 'rc-cafe', label: 'Cafés / salons de thé', parentCode: 'restauration-commerciale' },
    { code: 'rc-food-truck', label: 'Food truck / street food', parentCode: 'restauration-commerciale' },
    // Sous-secteurs : Restauration collective
    { code: 'rcl-scolaire', label: 'Restauration scolaire', parentCode: 'restauration-collective' },
    { code: 'rcl-entreprise', label: "Restauration d'entreprise", parentCode: 'restauration-collective' },
    { code: 'rcl-sante', label: 'Restauration santé / EHPAD', parentCode: 'restauration-collective' },
    { code: 'rcl-concedee', label: 'Restauration concédée', parentCode: 'restauration-collective' },
    // Sous-secteurs : Conseil & ingénierie (granularité plus fine)
    { code: 'ci-conseil-strategie', label: 'Conseil stratégique HCR', parentCode: 'conseil-ingenierie' },
    { code: 'ci-bureau-etudes', label: "Bureau d'études opérationnel", parentCode: 'conseil-ingenierie' },
    { code: 'ci-recrutement-rh', label: 'Recrutement / RH spécialisés HCR', parentCode: 'conseil-ingenierie' },
    // Sous-secteurs : Hospitalité élargie
    { code: 'he-traiteur', label: 'Traiteur / événementiel', parentCode: 'hospitalite-elargie' },
    { code: 'he-mice', label: 'MICE / séminaires', parentCode: 'hospitalite-elargie' },
    { code: 'he-spa', label: 'Spa & bien-être', parentCode: 'hospitalite-elargie' },
    { code: 'he-casino', label: 'Casino / jeux', parentCode: 'hospitalite-elargie' },
    { code: 'he-croisiere', label: 'Croisière', parentCode: 'hospitalite-elargie' },
    { code: 'he-aerien-ferro', label: 'Compagnies aériennes / ferroviaires', parentCode: 'hospitalite-elargie' },
];
// ─── Type d'établissement (multi-coche) ────────────────────────────────
// Indépendant du secteur : décrit la "culture" de la structure.
export const ESTABLISHMENT_KIND_OPTIONS = [
    { code: 'independant', label: 'Indépendant' },
    { code: 'chaine', label: 'Chaîne / groupe' },
    { code: 'familial', label: 'Familial' },
    { code: 'palace', label: 'Palace / luxe' },
    { code: 'international', label: 'International' },
    { code: 'saisonnier', label: 'Saisonnier' },
    { code: 'formation_active', label: 'Formation / apprentissage actif' },
    { code: 'rse_durable', label: 'RSE / durable / locavore' },
];
// ─── Disponibilité ─────────────────────────────────────────────────────
export const AVAILABILITY_TYPES = [
    { code: 'immediate', label: 'Immédiate' },
    { code: 'in_weeks', label: 'Dans quelques semaines' },
    { code: 'end_of_studies', label: 'À la fin de mes études' },
    { code: 'saison_ete', label: 'Saisonnier — été' },
    { code: 'saison_hiver', label: 'Saisonnier — hiver' },
];
// ─── Durée souhaitée ───────────────────────────────────────────────────
export const DESIRED_DURATIONS = [
    { code: 'short', label: 'Courte mission (≤ 3 mois)' },
    { code: 'medium', label: 'Moyenne (3 à 6 mois)' },
    { code: 'long', label: 'Longue (6 mois à 1 an)' },
    { code: 'permanent', label: 'Sans durée — projet à long terme' },
];
// ─── Horaires acceptés (multi-coche) ───────────────────────────────────
export const SCHEDULE_OPTIONS = [
    { code: 'journee', label: 'Journée' },
    { code: 'soiree', label: 'Soirée' },
    { code: 'nuit', label: 'Nuit' },
    { code: 'coupure', label: 'Service en coupure' },
    { code: 'sans_coupure', label: 'Service continu uniquement' },
    { code: 'weekend', label: 'Week-end & jours fériés' },
    { code: 'temps_partiel', label: 'Temps partiel' },
    { code: 'temps_plein', label: 'Temps plein' },
    { code: 'saisonnier_intensif', label: 'Saisonnier intensif (7j/7)' },
];
// ─── Mobilité ──────────────────────────────────────────────────────────
export const MOBILITY_RANGES = [
    { code: 'local', label: 'Local (ma ville uniquement)' },
    { code: 'km50', label: 'Rayon 50 km' },
    { code: 'km100', label: 'Rayon 100 km' },
    { code: 'km200', label: 'Rayon 200 km' },
    { code: 'regional', label: 'Régional' },
    { code: 'national', label: 'National' },
    { code: 'international', label: 'International' },
];
export const SESSION_COOKIE_NAME = 'lecomptoir.sid';
export const PASSWORD_MIN_LENGTH = 12;
export const INVITATION_EXPIRY_DAYS = 30;
export const PASSWORD_RESET_EXPIRY_MINUTES = 30;
/** Durée de session par défaut (étudiant, entreprise). */
export const SESSION_DURATION_HOURS = 8;
/** Durée de session réduite pour les rôles à privilèges (responsable, super_admin). */
export const SESSION_DURATION_ADMIN_HOURS = 4;
//# sourceMappingURL=index.js.map